import React, { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useUserDropdown } from "@/hooks/useUserDropdown";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useSuperadminRole } from "@/hooks/superadmin/useSuperadminRole";
import { useRecruiterRole } from "@/hooks/recruiter/useRecruiterRole";
import DropdownHeader from "./dropdown/DropdownHeader";
import NavigationItems from "./dropdown/NavigationItems";
import LogoutButton from "./dropdown/LogoutButton";
import DropdownTrigger from "./dropdown/DropdownTrigger";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface UserDropdownProps {
  isLoggingOut: boolean;
  onLogout: () => Promise<void>;
  isAdmin?: boolean;
}

const UserDropdown = ({
  isLoggingOut,
  onLogout,
  isAdmin: propsIsAdmin,
}: UserDropdownProps) => {
  const {
    user,
    isAdmin: hookIsAdmin,
    username,
    displayName,
    avatarUrl,
    getInitials,
    isOnDashboard,
    isOnProfilePage,
    isOnHomePage,
    isOnAdminPage,
    isOnSettingsPage,
    isOnNotificationsPage,
    isOnSuperadminPage,
    usernameSlug,
    isSuperadmin: profileIsSuperadmin,
  } = useUserDropdown();

  const { isAdmin: checkedIsAdmin } = useAdminCheck(user?.id);

  const { isSuperadmin, isLoading: isSuperadminLoading } = useSuperadminRole();

  const { isRecruiter, isLoading: isRecruiterLoading } = useRecruiterRole();

  const { toast } = useToast();
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const [verifiedStatus, setVerifiedStatus] = useState(false);

  const isAdmin =
    propsIsAdmin !== undefined
      ? propsIsAdmin
      : hookIsAdmin !== undefined
      ? hookIsAdmin
      : checkedIsAdmin;

  const resolvedIsSuperadmin = isSuperadmin || profileIsSuperadmin;

  const isMobile = useIsMobile();

  const safeAvatarUrl =
    avatarUrl && typeof avatarUrl === "string" && avatarUrl.trim() !== ""
      ? avatarUrl
      : null;

  const handleTripleClick = () => {
    setShowDebugMenu(true);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          role="button"
          className="cursor-pointer"
          onMouseDown={(e) => {
            if (e.detail === 3) {
              handleTripleClick();
            }
          }}
        >
          <DropdownTrigger
            avatarUrl={safeAvatarUrl}
            username={username}
            email={user?.email || null}
            getInitials={getInitials}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={`${
          isMobile ? "w-[calc(100vw-32px)] max-w-[280px]" : "w-64"
        } p-2 rounded-md shadow-lg bg-white border border-gray-200 z-50`}
        sideOffset={isMobile ? 4 : 8}
      >
        <DropdownHeader
          displayName={displayName}
          username={username}
          email={user?.email || null}
        />

        <DropdownMenuSeparator className="my-2 bg-gray-200/80" />

        <NavigationItems
          userId={user?.id}
          isAdmin={isAdmin}
          isSuperadmin={resolvedIsSuperadmin}
          isRecruiter={isRecruiter}
          isOnHomePage={isOnHomePage}
          isOnDashboard={isOnDashboard}
          isOnProfilePage={isOnProfilePage}
          isOnNotificationsPage={isOnNotificationsPage}
          isOnAdminPage={isOnAdminPage}
          isOnSettingsPage={isOnSettingsPage}
          isOnSuperadminPage={isOnSuperadminPage}
          username_slug={usernameSlug}
        />

        <DropdownMenuSeparator className="my-2 bg-gray-200/80" />

        <LogoutButton onLogout={onLogout} isLoggingOut={isLoggingOut} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
